import { makeAutoObservable } from 'mobx'
import { AxiosResponse } from 'axios'
import set from 'lodash.set'

import { notification } from 'services'
import { BabySettingsApi } from 'api'
import {
  BabyGender,
  IBaby,
  IBabySettings,
  IBabyNotification,
  Nullable,
  IBabyLastEvents,
} from 'types'
import { formatDateToISO } from 'utils'

import { serializeBaby, serializeNotification } from './utils'

const initialState: IBabySettings = {
  gender: BabyGender.MALE,
  name: '',
  birthDate: '',
  feedNotification: {
    hours: '3',
    minutes: '0',
  },
  sleepNotification: {
    hours: '0',
    minutes: '30',
  },
}

export default class BabySettingsStore {
  settings = initialState
  loading = true
  loaded = false
  lastEventsLoading = true
  babies: IBaby[] = []
  lastEvents: Nullable<IBabyLastEvents> = null
  babyNotifications: IBabyNotification[] = []
  selectedBaby: Nullable<IBaby> = null
  editableBaby: Nullable<IBaby> = null
  editableNotification: Nullable<IBabyNotification> = null

  constructor() {
    makeAutoObservable(this, undefined, { autoBind: true })
  }

  setSelectedBaby(baby: Nullable<IBaby>) {
    this.selectedBaby = baby
  }

  setEditableBaby(baby: Nullable<IBaby>) {
    this.editableBaby = baby
  }

  setEditableNotification(notification: Nullable<IBabyNotification>) {
    this.editableNotification = notification
  }

  changeSettings(field: string, value: any) {
    set(this.settings, field, value)
  }

  *getBabies() {
    try {
      const { data } = yield BabySettingsApi.getBabies()
      this.babies = data.data

      if (!this.selectedBaby) {
        this.setSelectedBaby(this.babies[0])
      }
    } catch {
      notification.error('Ошибка получения данных о детях')
    } finally {
      this.loading = false
      this.loaded = true
    }
  }

  *getBabyLastEvents() {
    if (this.selectedBaby) {
      this.lastEventsLoading = true

      try {
        const { data } = yield BabySettingsApi.getLastEvents(this.selectedBaby.id)
        this.lastEvents = data.data
      } catch {
        notification.error('Ошибка получения данных о последних событиях')
      } finally {
        this.lastEventsLoading = false
      }
    } else {
      this.lastEventsLoading = false
    }
  }

  *createBaby() {
    try {
      this.loading = true
      yield BabySettingsApi.createBaby(serializeBaby(this.settings))
      return true
    } catch {
      notification.error('Ошибка сохранения')
      return false
    } finally {
      this.loading = false
    }
  }

  *editBaby() {
    if (this.editableBaby) {
      this.loading = true

      try {
        const { name, birthDate, gender } = this.editableBaby

        yield BabySettingsApi.editBaby(this.editableBaby.id, {
          name,
          birthDate: formatDateToISO(birthDate),
          gender,
        })

        if (this.editableBaby.id === this.selectedBaby?.id) {
          this.setSelectedBaby({
            ...this.editableBaby,
            birthDate: formatDateToISO(birthDate),
          })
        }

        this.setEditableBaby(null)
        this.getBabies()
      } catch {
        notification.error('Ошибка изменения')
      } finally {
        this.loading = false
      }
    }
  }

  *deleteBaby(id: number) {
    try {
      this.loading = true
      yield BabySettingsApi.deleteBaby(id)
    } catch {
      notification.error('Ошибка удаления')
    } finally {
      this.loading = false
    }
  }

  *loadBabyNotifications() {
    if (this.selectedBaby) {
      this.loading = true

      try {
        const response: AxiosResponse<{ data: IBabyNotification[] }> =
          yield BabySettingsApi.getBabyNotifications(this.selectedBaby.id)

        this.babyNotifications = Array.isArray(response.data.data) ? response.data.data : []
      } catch {
        notification.error('Ошибка получения напоминаний')
      } finally {
        this.loading = false
      }
    }
  }

  *editBabyNotification() {
    if (this.selectedBaby && this.editableNotification) {
      this.loading = true

      try {
        yield BabySettingsApi.editBabyNotification(
          this.selectedBaby.id,
          this.editableNotification.id,
          serializeNotification(this.editableNotification),
        )
        this.babyNotifications = this.babyNotifications.map((notification) =>
          this.editableNotification && notification.id === this.editableNotification.id
            ? { ...this.editableNotification }
            : notification,
        )
        this.setEditableNotification(null)
      } catch {
        notification.error('Ошибка изменения')
      } finally {
        this.loading = false
      }
    }
  }
}
